import React, { useMemo, useState } from "react";
import { Namespace, useT } from "@racoon/i18n";
import { Button, Column, DataTable, Hyperlink, useModal, useToast } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import {
  useListOrgGroupsQuery,
  useCreateCustomGroupWithGrantsMutation,
  useLazyListGroupGrantsQuery,
  useDeleteCustomGroupMutation,
  useRestoreCustomGroupMutation,
} from "../../../../shared/api/custom-groups/custom-groups.api";
import { CreateAccessGroupForm } from "./create-access-group.form";
import {
  BunkerTypeStatusFilter,
  BunkerTypeStatus,
} from "../bunker-types-page/bunker-type-status-filter";
import { fromTimestamp } from "@racoon/lib";
import { useTableSettings } from "~/shared/hooks/useTableSettings";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { skipToken } from "@reduxjs/toolkit/query";

export const AccessGroupsSettingsTab: React.FC = () => {
  const t = useT();
  const { toast } = useToast();
  const { openModal, closeModal } = useModal();
  const currentOrgId = useAuthSession().activeCompanyId;
  const [statusFilter, setStatusFilter] = useState<BunkerTypeStatus>("active");
  const isArchive = statusFilter === "archived";
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    isArchive ? "access_groups_archived" : "access_groups",
    currentOrgId || undefined,
  );

  // A holding sees and edits the groups of every company below it; the
  // organization column tells them apart.
  const listArgs = currentOrgId
    ? { ownerOrganizationId: BigInt(currentOrgId), includeDescendants: true }
    : undefined;
  const { data, isLoading, refetch } = useListOrgGroupsQuery(listArgs ?? skipToken);
  // Archived = soft-deleted on the backend; both lists refetch on delete / restore.
  const { data: archivedData, isLoading: isArchivedLoading } = useListOrgGroupsQuery(
    listArgs ? { ...listArgs, deleted: true } : skipToken,
  );
  const activeGroups = data?.groups || [];
  const archivedGroups = archivedData?.groups || [];
  const groups = isArchive ? archivedGroups : activeGroups;
  const loading = isArchive ? isArchivedLoading : isLoading;

  const [deleteGroup] = useDeleteCustomGroupMutation();
  const [restoreGroup, { isLoading: isRestoring }] = useRestoreCustomGroupMutation();

  const confirmArchive = (group: ConnectProto.CustomGroup) => {
    const modalId = openModal({
      title: t(Namespace.Settings, "access_groups.dialog.archiveConfirmTitle"),
      closeButtonTop: "top-4",
      size: "lg",
      body: (
        <p className="text-center text-sm text-text-primary">
          {t(Namespace.Settings, "access_groups.dialog.archiveConfirmDescription", {
            name: group.name,
          })}
        </p>
      ),
      footer: (
        <div className="flex w-full justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => closeModal(modalId)}>
            {t(Namespace.Buttons, "cancel")}
          </Button>
          <Button
            size="sm"
            variant="opacityRedWithOutline"
            onClick={async () => {
              closeModal(modalId);
              try {
                await deleteGroup({ id: group.id }).unwrap();
                toast({ title: t(Namespace.Settings, "access_groups.dialog.archiveSuccess") });
              } catch {
                // GlobalErrorToaster shows the API error.
              }
            }}
          >
            {t(Namespace.Buttons, "archive")}
          </Button>
        </div>
      ),
    });
  };

  const handleRestore = async (group: ConnectProto.CustomGroup) => {
    try {
      await restoreGroup({ id: group.id }).unwrap();
      toast({ title: t(Namespace.Settings, "access_groups.dialog.restoreSuccess") });
    } catch {
      // GlobalErrorToaster shows the API error, CUSTOM_GROUP_NAME_TAKEN included.
    }
  };

  const handleOpenCreateModal = () => {
    const modalId = openModal({
      size: "custom",
      width: "75rem",
      height: "35rem",
      title: t(Namespace.Settings, "access_groups.dialog.createTitle"),
      bodyClassName: "!p-0 flex flex-col flex-1 min-h-0",
      body: <CreateAccessGroupForm onClose={() => closeModal(modalId)} onSuccess={refetch} />,
    });
  };

  const handleOpenEditModal = (group: any) => {
    const modalId = openModal({
      size: "custom",
      width: "75rem",
      height: "35rem",
      title: t(Namespace.Settings, "access_groups.dialog.editTitle"),
      bodyClassName: "!p-0 flex flex-col flex-1 min-h-0",
      body: (
        <CreateAccessGroupForm
          initialData={group}
          onClose={() => closeModal(modalId)}
          onSuccess={refetch}
          onArchive={(target) => {
            closeModal(modalId);
            confirmArchive(target);
          }}
        />
      ),
    });
  };

  const [createGroup] = useCreateCustomGroupWithGrantsMutation();
  const [triggerListGrants] = useLazyListGroupGrantsQuery();

  const handleDuplicate = async (group: any) => {
    try {
      const { grants } = await triggerListGrants({
        customGroupId: BigInt(group.id),
        organizationId: BigInt(group.ownerOrganizationId),
      }).unwrap();
      await createGroup({
        name: `${group.name} copy`,
        ownerOrganizationId: group.ownerOrganizationId,
        grants: (grants || []).map((g: any) => ({
          organizationId: g.organizationId,
          resourceType: g.resourceType,
          accessLevel: g.accessLevel,
        })),
      }).unwrap();
      toast({
        title: t(Namespace.Settings, "access_groups.dialog.duplicateSuccess"),
      });
      refetch();
    } catch {
      toast({
        title: t(Namespace.Settings, "access_groups.dialog.duplicateError"),
        variant: "destructive",
      });
    }
  };

  // An archived group can't be opened (the backend treats it as missing): restore is its only action.
  const columns = useMemo<Column<any>[]>(
    () => [
      {
        id: "name",
        header: t(Namespace.Settings, "access_groups.table.name"),
        key: "name",
        cell: (info) =>
          isArchive ? (
            <span className="font-medium">{info.getValue() as string}</span>
          ) : (
            <Hyperlink
              className="font-medium"
              onOpen={() => handleOpenEditModal(info.row.original)}
            >
              {info.getValue() as string}
            </Hyperlink>
          ),
      },
      {
        id: "organization",
        header: t(Namespace.Settings, "access_groups.table.organization"),
        key: "ownerOrganization",
        cell: (info) => {
          const org = info.getValue();
          return org?.name || "—";
        },
      },
      {
        id: "membersCount",
        // For an archived group: the members a restore brings back.
        header: t(
          Namespace.Settings,
          isArchive ? "access_groups.table.restoresMembers" : "access_groups.table.membersCount",
        ),
        key: "groupMembershipCount",
        cell: (info) => {
          const group = info.getValue();
          return <span>{group}</span>;
        },
      },
      {
        id: isArchive ? "deletedAt" : "createdAt",
        header: t(
          Namespace.Settings,
          isArchive ? "access_groups.table.archivedAt" : "access_groups.table.createdAt",
        ),
        key: isArchive ? "deletedAt" : "createdAt",
        cell: (info) => {
          const val = info.getValue();
          return val ? fromTimestamp(val)?.toLocaleDateString() : "";
        },
      },

      {
        id: "actions",
        header: t(Namespace.Settings, "access_groups.table.actions"),
        width: 150,
        cell: (info) => {
          const group = info.row.original;
          return (
            <Button
              variant="outline"
              className="invisible group-hover:visible bg-white border-none text-brand-blue transition-colors"
              disabled={isArchive && isRestoring}
              onClick={() => (isArchive ? handleRestore(group) : handleDuplicate(group))}
            >
              {isArchive
                ? t(Namespace.Buttons, "restore")
                : t(Namespace.Settings, "access_groups.dialog.duplicate")}
            </Button>
          );
        },
      },
    ],
    [t, isArchive, isRestoring, handleOpenEditModal, handleDuplicate, handleRestore],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <BunkerTypeStatusFilter
        activeStatus={statusFilter}
        onStatusChange={setStatusFilter}
        counts={{ active: activeGroups.length, archived: archivedGroups.length }}
        onCreateClick={handleOpenCreateModal}
      />

      <div className="flex min-h-0 flex-1 bg-white">
        {groups.length === 0 && !loading ? (
          <div className="flex items-center justify-center h-full text-gray-500 py-12">
            {t(Namespace.Settings, "access_groups.table.empty")}
          </div>
        ) : (
          <DataTable
            // Column state is read once on mount, so each tab gets its own table.
            key={statusFilter}
            className=""
            columns={columns}
            data={groups}
            loading={loading}
            rowKey={(row) => row.id?.toString() || Math.random().toString()}
            rowClassName={() => "group"}
            columnConfig={columnConfig}
            onColumnConfigChange={handleColumnConfigChange}
            namespace={Namespace.Settings}
          />
        )}
      </div>
    </div>
  );
};
